import { TabState, DatasetId, createDatasetId, DataSet, Report } from '../../types';
import { TabRegistry } from './TabRegistry';
import logger from '../utils/logger';

export interface TabCreationContext {
  datasets: Record<string, DataSet>;
  reports: Report[];
  activeDatasetId?: DatasetId;
}

export interface TabCreationResult {
  tab: TabState | null;
  error?: string;
}

/**
 * 汎用的なタブ作成ヘルパー関数
 * TabContainerとTabCreationModalの両方で使用できる共通ロジック
 */
export function createTabWithContext(
  tabType: string,
  context: TabCreationContext,
  selectedItemId?: string
): TabCreationResult {
  const registryEntry = TabRegistry[tabType as keyof typeof TabRegistry];
  
  if (!registryEntry) {
    logger.error('[TabCreationHelper] Unknown tab type:', { tabType });
    return { tab: null, error: `Unknown tab type: ${tabType}` };
  }

  try {
    // データテーブルタブの場合
    if (tabType === 'data-table' && selectedItemId) {
      const datasetId = createDatasetId(selectedItemId);
      const dataset = context.datasets[datasetId];
      
      if (!dataset) {
        logger.error('[TabCreationHelper] Dataset not found:', { datasetId });
        return { tab: null, error: `Dataset not found: ${datasetId}` };
      }
      
      const tab = registryEntry.createTab(datasetId, dataset.name);
      return { tab };
    }

    // レポートタブの場合
    if (tabType === 'report' && selectedItemId) {
      if (selectedItemId === '__new__') {
        // 新規レポート作成
        const reportId = `report-${Date.now()}`;
        const tab = registryEntry.createTab(reportId, 'New Report');
        return { tab };
      } else {
        // 既存レポートを開く
        const report = context.reports.find(r => r.id === selectedItemId);
        if (!report) {
          logger.error('[TabCreationHelper] Report not found:', { selectedItemId });
          return { tab: null, error: `Report not found: ${selectedItemId}` };
        }
        const tab = registryEntry.createTab(selectedItemId, report.name);
        return { tab };
      }
    }

    // モデル詳細タブの場合（将来の拡張用）
    if (tabType === 'model-detail' && selectedItemId) {
      // モデル詳細の実装は後で追加
      const tab = registryEntry.createTab(selectedItemId, `Model ${selectedItemId}`);
      return { tab };
    }

    // その他のタブ（単純な作成）
    const tab = registryEntry.createTab();
    
    // アクティブなデータセットIDがある場合はメタデータに設定
    if (tab && context.activeDatasetId && needsDatasetContext(tabType)) {
      tab.metadata = { ...tab.metadata, datasetId: context.activeDatasetId };
    }
    
    return { tab };
  } catch (error) {
    logger.error('[TabCreationHelper] Error creating tab:', { tabType, error });
    return { 
      tab: null, 
      error: error instanceof Error ? error.message : 'Failed to create tab' 
    };
  }
}

/**
 * タブタイプがデータセットコンテキストを必要とするかチェック
 */
function needsDatasetContext(tabType: string): boolean {
  const contextRequiredTabs = [
    'statistics',
    'excluded-rows',
    'sql-editor',
    'selected-rows',
    'crosstab',
    'graph-builder'
  ];

  return contextRequiredTabs.includes(tabType);
}

/**
 * 選択可能なアイテムを取得する
 */
export interface SelectableItem {
  id: string;
  name: string;
  description?: string;
}

export function getSelectableItems(
  tabType: string,
  context: TabCreationContext
): SelectableItem[] {
  switch (tabType) {
    case 'data-table':
      return Object.entries(context.datasets)
        .map(([id, dataset]) => ({
          id,
          name: dataset.name,
          description: `${dataset.rowCount} rows × ${dataset.schema.columns.length} columns`
        }));
      
    case 'report': {
      const items: SelectableItem[] = [
        {
          id: '__new__',
          name: 'New Report',
          description: 'Create a new empty report'
        }
      ];
      
      context.reports.forEach(report => {
        items.push({
          id: report.id,
          name: report.name,
          description: report.description
        });
      });
      
      return items;
    }
      
    default:
      return [];
  }
}

/**
 * タブタイプが選択を必要とするかチェック
 */
export function requiresSelection(tabType: string): boolean {
  return ['data-table', 'report', 'model-detail'].includes(tabType);
}

/**
 * タブ作成オプションを生成
 */
export interface TabCreationOption {
  id: string;
  label: string;
  icon: string;
  description?: string;
  requiresSelection?: boolean;
  disabled?: boolean;
}

export function getTabCreationOptions(): TabCreationOption[] {
  return Object.entries(TabRegistry)
    .filter(([_, entry]) => entry.showInCommandPalette !== false)
    .map(([type, entry]) => ({
      id: type,
      label: entry.label || type,
      icon: entry.icon || '📄',
      description: entry.description,
      requiresSelection: requiresSelection(type),
      disabled: false
    }));
}